import {
  eachDayOfInterval,
  endOfDay,
  startOfDay,
  startOfToday,
  startOfTomorrow,
  subDays,
} from 'date-fns';
import { defineStore } from 'pinia';
import { Subject } from 'rxjs';

import { useOrderStore } from '@/features/orders/orderStore';
import { useProjectContributorStore } from '@/features/projectContributors/store/projectContributorStore';
import { useProjectTradeStore } from '@/features/projectTrades/projectTradeStore';

import { useLocalOrderSchedulingEngineUtils } from '../schedule/engine/install';
import { SummaryRowCellMouseEvent } from './types';
import {
  buildDailyTradeSummaries,
  DailyTradeSummariesResult,
  formatDateKey,
  getVisibleOrdersMap,
} from './utils';

type TimespanAggregationResult = { dateKey: string | null; count: number };

export const useCapacityStore = defineStore('capacity-store', () => {
  const { isStartDateDuringWorkingTime } = useLocalOrderSchedulingEngineUtils();
  const contributorStore = useProjectContributorStore();
  const orderStore = useOrderStore();
  const projectTradeStore = useProjectTradeStore();

  const summaryRowRendered$ = new Subject<string>();
  const summaryRowCellMouseEvents$ = new Subject<SummaryRowCellMouseEvent>();
  const canShowCapacities = ref(false);

  const setCanShowCapacities = (value: boolean) => {
    canShowCapacities.value = value;
  };

  let dailyAggregationCache = new Map<
    string,
    {
      sumOfActivities: TimespanAggregationResult;
      sumOfWorkers: TimespanAggregationResult;
    }
  >();

  let timespanAggregationCache = new Map<
    string,
    {
      maxSumOfWorkers: TimespanAggregationResult;
      maxSumOfActivities: TimespanAggregationResult;
    }
  >();

  const clearCaches = () => {
    dailyAggregationCache = new Map();
    timespanAggregationCache = new Map();
  };

  const tradeSummariesData = computed<DailyTradeSummariesResult>(() => {
    if (!canShowCapacities.value) {
      return {
        dailySummaries: {},
        maxTradeAppearances: {},
        maxTradeWorkerCounts: {},
        timeRange: {
          start: startOfToday(),
          end: startOfTomorrow(),
        },
        tradesUsed: new Map(),
      };
    }

    clearCaches();

    const visibleOrders = getVisibleOrdersMap(orderStore.orders);
    return buildDailyTradeSummaries(
      visibleOrders,
      projectTradeStore.trades,
      contributorStore.contributorGroups,
      isStartDateDuringWorkingTime,
    );
  });

  const dailyTradeSummaries = computed(() => tradeSummariesData.value.dailySummaries);
  const maxTradeAppearances = computed(() => tradeSummariesData.value.maxTradeAppearances);
  const maxTradeWorkerCounts = computed(() => tradeSummariesData.value.maxTradeWorkerCounts);
  const summaryTimeRange = computed(() => tradeSummariesData.value.timeRange);
  const tradesUsedForSummaries = computed(() => tradeSummariesData.value.tradesUsed);

  const aggregateTradeSummariesForDate = (
    date: Date,
  ): {
    sumOfWorkers: TimespanAggregationResult;
    sumOfActivities: TimespanAggregationResult;
  } => {
    const dateKey = formatDateKey(date);

    const cachedAggregation = dailyAggregationCache.get(dateKey);
    if (cachedAggregation) return cachedAggregation;

    const sumOfActivities: TimespanAggregationResult = { dateKey, count: 0 };
    const sumOfWorkers: TimespanAggregationResult = { dateKey, count: 0 };

    const trades = dailyTradeSummaries.value[dateKey] || [];

    trades.forEach((trade) => {
      sumOfActivities.count += trade.count;
      sumOfWorkers.count += trade.workerCount;
    });

    dailyAggregationCache.set(dateKey, { sumOfActivities, sumOfWorkers });

    return { sumOfActivities, sumOfWorkers };
  };

  const aggregateTradeSummariesForTimespan = (
    startDate: Date,
    endDate: Date,
  ): {
    maxSumOfWorkers: TimespanAggregationResult;
    maxSumOfActivities: TimespanAggregationResult;
  } => {
    const intervalStart = startOfDay(startDate);
    const intervalEnd = endOfDay(subDays(endDate, 1));

    const timespanAggregationCacheKey = `${formatDateKey(intervalStart)}-${formatDateKey(intervalEnd)}`;
    const cachedAggregationResult = timespanAggregationCache.get(timespanAggregationCacheKey);
    if (cachedAggregationResult) return cachedAggregationResult;

    const daysInRange = eachDayOfInterval({
      start: intervalStart,
      end: intervalEnd,
    });

    const maxSumOfActivities: TimespanAggregationResult = { dateKey: null, count: 0 };
    const maxSumOfWorkers: TimespanAggregationResult = { dateKey: null, count: 0 };

    daysInRange.forEach((day) => {
      const dateKey = formatDateKey(day);
      const trades = dailyTradeSummaries.value[dateKey] || [];
      let totalActivityCount = 0;
      let totalWorkerCount = 0;

      trades.forEach((trade) => {
        totalActivityCount += trade.count;
        totalWorkerCount += trade.workerCount;
      });

      if (totalActivityCount > maxSumOfActivities.count) {
        maxSumOfActivities.dateKey = dateKey;
        maxSumOfActivities.count = totalActivityCount;
      }
      if (totalWorkerCount > maxSumOfWorkers.count) {
        maxSumOfWorkers.dateKey = dateKey;
        maxSumOfWorkers.count = totalWorkerCount;
      }
    });

    const result = {
      maxSumOfWorkers,
      maxSumOfActivities,
    };

    timespanAggregationCache.set(timespanAggregationCacheKey, result);

    return result;
  };

  /**
   * Maximum values across all days in the project
   */
  const capacityPeaks = computed(() => {
    let activities = 0;
    let workers = 0;

    for (const trades of Object.values(dailyTradeSummaries.value)) {
      let dayTotalWorkers = 0;
      let dayTotalActivities = 0;

      trades.forEach((trade) => {
        dayTotalWorkers += trade.workerCount;
        dayTotalActivities += trade.count;
      });

      if (dayTotalWorkers > workers) workers = dayTotalWorkers;
      if (dayTotalActivities > activities) activities = dayTotalActivities;
    }

    return { workers, activities };
  });

  return {
    capacityPeaks,
    dailyTradeSummaries,
    maxTradeAppearances,
    maxTradeWorkerCounts,
    setCanShowCapacities,
    summaryTimeRange,
    tradesUsedForSummaries,
    summaryRowCellMouseEvents$,
    summaryRowRendered$,
    aggregateTradeSummariesForDate,
    aggregateTradeSummariesForTimespan,
  };
});

export type CapacityStore = ReturnType<typeof useCapacityStore>;
